import { create } from 'zustand'
import { commitApi } from '@/api/commitApi'
import { metadataToEditorState } from '@/services/metadata/adapters'
import { keyedToArray } from '@/services/metadata/keyed'
import { ApiError } from '@/api/httpClient'
import type { CommitListItem, CommitDetail } from '@/api/types'
import type { ProjectMetadataV1 } from '@/services/metadata/schema'
import { useDiagramStore } from '@/stores/diagramStore'
import { useHistoryStore } from '@/stores/historyStore'
import { useTabViewStore } from '@/stores/tabViewStore'
import { useTabsStore } from '@/stores/tabsStore'
import { useViewportStore } from '@/stores/viewportStore'

interface CommitsStore {
  commits: CommitListItem[]
  currentCommit: CommitDetail | null
  historyTruncated: boolean
  loading: boolean
  error: string | null

  loadCommits: (projectId: number) => Promise<void>
  createCommit: (
    projectId: number,
    opts: { name: string; description?: string; baseCommitId: number | null },
  ) => Promise<CommitDetail>
  restoreCommit: (projectId: number, commitId: number) => Promise<void>
  returnToDraft: (projectId: number) => Promise<void>
  reset: () => void
}

function hydrateEditorFromMetadata(metadata: ProjectMetadataV1) {
  const state = metadataToEditorState(metadata)
  useDiagramStore.getState().loadDiagram(state.diagram)
  useViewportStore.getState().setViewport(state.viewport.sc, state.viewport.tx, state.viewport.ty)
  useTabViewStore.getState().setHiddenTableIds(state.tabView.hiddenTableIds)
  useHistoryStore.getState().initHistory({
    ...state.diagram,
    hiddenTableIds: state.tabView.hiddenTableIds,
  })
}

export const useCommitsStore = create<CommitsStore>((set) => ({
  commits: [],
  currentCommit: null,
  historyTruncated: false,
  loading: false,
  error: null,

  async loadCommits(projectId) {
    set({ loading: true, error: null, commits: [] })
    try {
      const resp = await commitApi.list(projectId)
      const inner = resp.results
      set({
        commits: Array.isArray(inner?.results) ? inner.results : [],
        historyTruncated: inner?.history_truncated_by_plan ?? false,
        loading: false,
      })
    } catch (err) {
      set({ loading: false, error: errMsg(err) })
    }
  },

  async createCommit(projectId, { name, description = '', baseCommitId }) {
    const { useDraftStore } = await import('./draftStore')

    const commit = await commitApi.create(projectId, {
      name,
      description,
      base_commit_id: baseCommitId,
    }, { silent: true })

    // Update local list
    set((s) => ({
      currentCommit: commit,
      commits: [commit, ...s.commits],
    }))

    const draft = await useDraftStore.getState().loadDraft(projectId)
    useDraftStore.setState({
      draft,
      baseCommitId: draft.base_commit,
      rev: draft.rev,
      metadata: draft.metadata as never,
      isDirty: false,
      mode: 'draft',
      viewingCommitId: null,
    })

    const activeTabId = useTabsStore.getState().activeTabId
    if (activeTabId) useTabsStore.getState().markClean(activeTabId)

    return commit
  },

  async restoreCommit(projectId, commitId) {
    const { useDraftStore } = await import('./draftStore')
    const commit = await commitApi.get(projectId, commitId)

    set((s) => ({
      currentCommit: commit,
      commits: s.commits.some((c) => c.id === commit.id)
        ? s.commits
        : [commit, ...s.commits],
    }))

    if (commit.metadata) {
      hydrateEditorFromMetadata(commit.metadata as ProjectMetadataV1)
    }
    useDraftStore.getState().setMode('view', commit.id)
  },

  async returnToDraft(projectId) {
    const { useDraftStore } = await import('./draftStore')
    const draft = await useDraftStore.getState().loadDraft(projectId)
    hydrateEditorFromMetadata(draftApiMetadataToEditorMetadata(draft.metadata))
  },

  reset() {
    set({ commits: [], currentCommit: null, historyTruncated: false, loading: false, error: null })
  },
}))

function draftApiMetadataToEditorMetadata(metadata: unknown): ProjectMetadataV1 {
  return keyedToArray(metadata as never)
}

function errMsg(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Erro desconhecido'
}
